import React, { useState, useEffect, useCallback, useRef } from 'react'
import {
  CheckCircle2, ChevronRight, ChevronDown,
  Upload, Eye, Plus, Shield,
  Paperclip, X,
} from 'lucide-react'
import { getLocalApiUrl, authFetch } from '../../../config/api'
import QuickChecksPanel from './QuickChecksPanel'

const P = { pri: '#0a5169', priM: '#e8f2f6' }

const ITEM_STATUS = {
  not_started: { label: 'Not Started', color: '#6B7280', bg: '#f8fafc', ring: '#e5e7eb' },
  in_progress: { label: 'In Progress', color: '#ca8a04', bg: '#fefce8', ring: '#fef08a' },
  done: { label: 'Done', color: '#16a34a', bg: '#f0fdf4', ring: '#bbf7d0' },
  na: { label: 'N/A', color: '#6B7280', bg: '#f3f4f6', ring: '#e5e7eb' },
}

/** Required checklist rows are satisfied by Done or N/A (not applicable). */
function isMandatoryResolved(status) {
  return status === 'done' || status === 'na'
}

const INITIAL_BUCKETS = [
  {
    id: 'sof',
    title: 'Source of Funds / Source of Wealth',
    icon: '💰',
    description: "Identify where the customer's money comes from — both for specific transactions and their general wealth.",
    items: [
      { id: 'sof_1', label: 'Request source of funds declaration from patron', guidance: 'Ask the patron to explain the source of funds for recent gaming activity.', mandatory: true, status: 'not_started', findings: '', docs: [] },
      { id: 'sof_2', label: 'Verify source of funds documentation', guidance: 'Review any supporting documents provided.', mandatory: true, status: 'not_started', findings: '', docs: [] },
      { id: 'sof_3', label: 'Assess source of wealth (overall)', guidance: "For PEPs, assess the customer's overall wealth.", mandatory: true, status: 'not_started', findings: '', docs: [] },
    ],
  },
  {
    id: 'verify',
    title: 'Additional Identification / Re-verification',
    icon: '🔍',
    description: 'Collect additional identity information or re-verify existing KYC.',
    items: [
      { id: 'ver_1', label: 'Re-verify existing KYC documents', guidance: 'Confirm the current KYC record is still accurate.', mandatory: false, status: 'not_started', findings: '', docs: [], dataSource: null, isKyc: true },
      { id: 'ver_2', label: 'Collect additional identification (occupation, employer)', guidance: "For PEPs, collect details of their political role.", mandatory: true, status: 'not_started', findings: '', docs: [], dataSource: null, isKyc: true },
    ],
  },
  {
    id: 'txn',
    title: 'Transaction Analysis',
    icon: '📊',
    description: "Detailed analysis of the customer's gaming and transaction history.",
    items: [
      { id: 'txn_1', label: 'Review payout history (last 12 months)', guidance: 'View payout history from the system. Filter, export to XLS and attach if needed.', mandatory: false, status: 'not_started', findings: '', docs: [], dataSource: 'payout_history' },
      { id: 'txn_2', label: 'Review gaming session data', guidance: 'View gaming session data from the system.', mandatory: false, status: 'not_started', findings: '', docs: [], dataSource: 'gaming_session' },
    ],
  },
  {
    id: 'approval',
    title: 'Senior Management Approval',
    icon: '✅',
    description: 'For foreign PEPs: senior management must approve whether to continue providing services.',
    items: [
      { id: 'app_1', label: 'Prepare recommendation for management', guidance: 'Summarise findings from all ECDD measures.', mandatory: true, status: 'not_started', findings: '', docs: [] },
      { id: 'app_2', label: 'Obtain senior management approval', guidance: 'Submit the completed ECDD package for review.', mandatory: true, status: 'not_started', findings: '', docs: [] },
    ],
  },
]

function Badge({ bg, color, border, children }) {
  return (
    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap"
      style={{ backgroundColor: bg, color, border: border ? `1px solid ${border}` : undefined }}>
      {children}
    </span>
  )
}

function ChecklistItem({ caseId, item, onStatusChange, onFindingsChange, onDocumentsChange, savingId }) {
  const [expanded, setExpanded] = useState(item.status === 'in_progress')
  const [draft, setDraft] = useState(item.findings || '')
  const [uploading, setUploading] = useState(false)
  const st = ITEM_STATUS[item.status] || ITEM_STATUS.not_started
  const fileInputRef = useRef(null)

  useEffect(() => { setDraft(item.findings || '') }, [item.findings])

  const handleFilesSelected = async (e) => {
    const files = e.target.files
    if (!files?.length) return
    if (!caseId) {
      alert('Cannot upload: case not loaded yet')
      e.target.value = ''
      return
    }

    setUploading(true)
    try {
      const uploadedNames = []
      for (const file of Array.from(files)) {
        const fd = new FormData()
        fd.append('file', file)
        const res = await authFetch(
          getLocalApiUrl(`/api/cases/${encodeURIComponent(caseId)}/forms/ecdd/documents`),
          { method: 'POST', body: fd }
        )
        if (res.ok) {
          uploadedNames.push(file.name)
        } else {
          console.error(`Upload failed for ${file.name}: HTTP ${res.status}`)
        }
      }
      if (uploadedNames.length > 0 && onDocumentsChange) {
        onDocumentsChange(item.id, uploadedNames)
      }
      if (uploadedNames.length < files.length) {
        alert(`Uploaded ${uploadedNames.length} of ${files.length} file(s). See console for details.`)
      }
    } catch (err) {
      console.error('ECDD upload failed:', err)
      alert('Failed to upload one or more files')
    } finally {
      setUploading(false)
      e.target.value = ''
    }
  }

  return (
    <div className="rounded-lg mb-2 overflow-hidden border"
      style={{ borderColor: item.mandatory && !isMandatoryResolved(item.status) ? '#fecaca' : '#f1f5f9', background: item.status === 'done' ? '#fafffe' : '#fff' }}>
      <div onClick={() => setExpanded(!expanded)} className="flex items-center gap-3 p-3 cursor-pointer hover:bg-gray-50/50">
        <div className="w-5 h-5 rounded-full flex-shrink-0 flex items-center justify-center text-xs font-bold"
          style={{ border: `2px solid ${st.ring}`, background: item.status === 'done' ? '#16a34a' : 'transparent', color: '#fff' }}>
          {item.status === 'done' && '✓'}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex gap-2 items-center flex-wrap">
            <span className={`text-sm font-semibold ${item.status === 'done' ? 'text-green-600' : 'text-slate-800'}`}>{item.label}</span>
            {item.mandatory && <Badge bg="#fef2f2" color="#dc2626" border="#fecaca">Required</Badge>}
          </div>
        </div>
        <div
          className="relative flex-shrink-0 flex items-center"
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <select
            value={item.status}
            onChange={(e) => onStatusChange(item.id, e.target.value)}
            aria-label={`Status: ${item.label}`}
            className="appearance-none text-xs font-semibold rounded-full border pl-2.5 pr-7 py-1 cursor-pointer max-w-[9.5rem] truncate box-border"
            style={{ color: st.color, backgroundColor: st.bg, borderColor: st.ring }}
          >
            {['not_started', 'in_progress', 'done', 'na'].map((s) => (
              <option key={s} value={s}>{ITEM_STATUS[s].label}</option>
            ))}
          </select>
          <ChevronDown size={14} className="text-slate-500 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none" />
        </div>
        {item.docs?.length > 0 && <Paperclip size={14} className="text-slate-400" />}
        {expanded ? <ChevronDown size={14} className="text-slate-400" /> : <ChevronRight size={14} className="text-slate-400" />}
      </div>
      {expanded && (
        <div className="px-4 pb-4 pt-0 ml-8 border-t border-slate-50">
          <div className="p-3 bg-slate-50 rounded-lg mt-3 mb-3 border border-slate-100">
            <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Guidance</div>
            <div className="text-sm text-slate-500 leading-relaxed">{item.guidance}</div>
          </div>
          <div className="mb-3">
            <div className="text-xs font-semibold text-slate-600 mb-1.5">Findings / Notes</div>
            <textarea value={draft} onChange={e => setDraft(e.target.value)}
              onBlur={() => { if (draft !== (item.findings || '')) onFindingsChange(item.id, draft) }}
              placeholder="Record what was found, observed, or confirmed..."
              className="w-full min-h-[140px] p-3 rounded-lg border border-slate-200 text-sm text-slate-700 bg-white resize-y box-border" />
            {savingId === item.id && <span className="text-[11px] text-slate-400">Saving…</span>}
          </div>
          {item.docs?.length > 0 && (
            <div className="mb-3">
              <div className="text-xs font-semibold text-slate-600 mb-1.5">Attachments</div>
              <div className="flex gap-2 flex-wrap">
                {item.docs.map((d, i) => (
                  <span key={i} className="flex gap-1.5 items-center px-2.5 py-1.5 rounded-md bg-slate-100 text-xs text-slate-600">
                    <Paperclip size={12} />{typeof d === 'string' ? d : d.file_name || 'file'}
                  </span>
                ))}
              </div>
            </div>
          )}
          {!item.dataSource && !item.isKyc && (
            <div className="mb-1">
              <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                multiple
                onChange={handleFilesSelected}
                disabled={uploading}
              />
              <button
                type="button"
                className="inline-flex items-center gap-1.5 text-xs py-1.5 px-3 rounded-md border font-semibold cursor-pointer disabled:opacity-50"
                style={{ borderColor: P.pri, background: P.priM, color: P.pri }}
                onClick={(e) => {
                  e.stopPropagation()
                  fileInputRef.current?.click()
                }}
                disabled={uploading}
              >
                <Upload size={12} /> {uploading ? 'Uploading…' : 'Upload Document'}
              </button>
            </div>
          )}
          {item.dataSource && (
            <button type="button" className="inline-flex items-center gap-1.5 text-xs py-1.5 px-3 rounded-md border font-semibold cursor-pointer"
              style={{ borderColor: P.pri, background: P.priM, color: P.pri }}>
              <Eye size={12} /> {item.dataSource === 'payout_history' ? 'View Payout History' : 'View Gaming Sessions'}
            </button>
          )}
        </div>
      )}
    </div>
  )
}

function BucketSection({ caseId, bucket, onStatusChange, onFindingsChange, onDocumentsChange, savingId }) {
  const [collapsed, setCollapsed] = useState(true)
  const done = bucket.items.filter(i => i.status === 'done' || i.status === 'na').length
  const total = bucket.items.length
  const mandatoryRemaining = bucket.items.filter(i => i.mandatory && !isMandatoryResolved(i.status)).length
  const isComplete = done === total

  return (
    <div className="bg-white border border-slate-200 rounded-lg overflow-hidden mb-3">
      <div onClick={() => setCollapsed(!collapsed)}
        className="flex items-center gap-3 px-4 py-3.5 cursor-pointer hover:bg-slate-50/50 transition-colors">
        <div className="w-6 h-6 rounded-full flex-shrink-0 flex items-center justify-center"
          style={{ backgroundColor: isComplete ? '#f0fdf4' : '#f8fafc', border: `2px solid ${isComplete ? '#16a34a' : '#e5e7eb'}`, color: isComplete ? '#16a34a' : '#6B7280' }}>
          {isComplete ? <CheckCircle2 size={14} /> : <span className="w-2 h-2 rounded-full bg-slate-300" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex gap-2 items-center flex-wrap">
            <span className="text-sm">{bucket.icon}</span>
            <span className="text-sm font-bold" style={{ color: P.pri }}>{bucket.title}</span>
            <span className={`text-xs font-mono ${isComplete ? 'text-green-600' : 'text-slate-500'}`}>{done}/{total}</span>
            {mandatoryRemaining > 0 && <Badge bg="#fef2f2" color="#dc2626" border="#fecaca">{mandatoryRemaining} required</Badge>}
          </div>
          <div className="text-xs text-slate-500 mt-0.5 truncate">{bucket.description}</div>
        </div>
        {collapsed ? <ChevronRight size={14} className="text-slate-400" /> : <ChevronDown size={14} className="text-slate-400" />}
      </div>
      {!collapsed && (
        <div className="px-4 pb-4 pt-1 border-t border-slate-50">
          {bucket.items.map(item => (
            <ChecklistItem key={item.id} caseId={caseId} item={item} onStatusChange={onStatusChange} onFindingsChange={onFindingsChange} onDocumentsChange={onDocumentsChange} savingId={savingId} />
          ))}
        </div>
      )}
    </div>
  )
}

const DECISION_OPTIONS = [
  { key: 'satisfactory', label: 'Close — Satisfactory', desc: 'No further action required', color: '#16a34a' },
  { key: 'enhanced_monitoring', label: 'Enhanced Monitoring', desc: 'Keep under periodic review', color: '#ca8a04' },
  { key: 'escalated_smr', label: 'Escalate → SMR', desc: 'File suspicious matter report', color: '#dc2626' },
]

// S8-F1c — skip reason enum for mandatory ECDD items marked as N/A.
// Must stay in sync with ALLOWED_SKIP_CODES in backend/src/routes/cases.js
// (POST /api/cases/:id/forms/ecdd).
const SKIP_REASON_OPTIONS = [
  { value: 'patron_deceased',             label: 'Patron deceased — unable to complete' },
  { value: 'patron_refused',              label: 'Patron refused to provide information' },
  { value: 'information_already_verified', label: 'Information already verified in prior KYC / ECDD cycle' },
  { value: 'exemption_applies',           label: 'AML or threshold exemption applies' },
  { value: 'case_closed_satisfactory',    label: 'Case closed satisfactory before reaching this step' },
  { value: 'other',                       label: 'Other (notes required)' },
]
const MIN_SKIP_NOTES_CHARS = 10

function SkipReasonModal({ open, itemLabel, submitting, onCancel, onConfirm }) {
  const [code, setCode] = useState('')
  const [notes, setNotes] = useState('')
  useEffect(() => { if (!open) { setCode(''); setNotes('') } }, [open])
  if (!open) return null
  const trimmedLen = notes.trim().length
  const notesMissing = code === 'other' && trimmedLen < MIN_SKIP_NOTES_CHARS
  const canSubmit = !submitting && !!code && !notesMissing
  const activeOption = SKIP_REASON_OPTIONS.find((o) => o.value === code)
  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center p-5" style={{ background: 'rgba(15,23,42,0.55)' }}>
      <div className="w-full max-w-[540px] bg-white rounded-xl shadow-2xl overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-200">
          <div className="text-base font-bold text-slate-900">Mark as N/A — reason required</div>
          <div className="text-xs text-slate-500 mt-0.5">
            SVCP §5.2 — skipping a mandatory ECDD item ({itemLabel || 'this step'}) needs a defensible category.
          </div>
        </div>
        <div className="px-5 py-4 flex flex-col gap-3.5">
          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1.5">
              Skip reason <span className="text-red-600">*</span>
            </label>
            <select
              value={code}
              onChange={(e) => setCode(e.target.value)}
              disabled={submitting}
              className="w-full px-2 py-2 text-sm rounded border border-slate-300 bg-white"
            >
              <option value="">Select a reason…</option>
              {SKIP_REASON_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
            {activeOption && code !== 'other' && (
              <div className="text-xs text-slate-500 mt-1.5">This reason will be captured against the item and visible on the audit trail.</div>
            )}
          </div>
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-xs font-semibold text-slate-700">
                Supporting notes {code === 'other' && <span className="text-red-600">*</span>}
              </label>
              <span
                className={`text-[11px] font-mono ${
                  code === 'other' ? (trimmedLen >= MIN_SKIP_NOTES_CHARS ? 'text-green-600' : 'text-red-700') : 'text-slate-400'
                }`}
              >
                {code === 'other' ? `${trimmedLen} / ${MIN_SKIP_NOTES_CHARS} min` : 'optional'}
              </span>
            </div>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              disabled={submitting}
              rows={3}
              placeholder={code === 'other' ? 'Describe why none of the structured categories apply.' : 'Optional — additional context about the skip decision.'}
              className="w-full px-2.5 py-2 text-sm rounded border border-slate-300 box-border"
            />
          </div>
        </div>
        <div className="px-5 py-3 border-t border-slate-200 bg-slate-50 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={submitting}
            className="px-4 py-2 text-sm font-semibold text-slate-700 bg-white border border-slate-300 rounded"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={!canSubmit}
            onClick={() => canSubmit && onConfirm({ code, notes: notes.trim() || undefined })}
            className={`px-4 py-2 text-sm font-bold text-white rounded ${canSubmit ? 'bg-amber-600 hover:bg-amber-700' : 'bg-slate-300 cursor-not-allowed'}`}
          >
            {submitting ? 'Saving…' : 'Mark N/A'}
          </button>
        </div>
      </div>
    </div>
  )
}

export default function EcddInlinePanel({ caseId, memberId, memberName }) {
  const [buckets, setBuckets] = useState(INITIAL_BUCKETS)
  const [decision, setDecision] = useState(null)
  const [decisionRationale, setDecisionRationale] = useState('')
  const [saving, setSaving] = useState(false)
  const [savingItemId, setSavingItemId] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const [loading, setLoading] = useState(true)

  // Load ECDD draft from API
  const loadDraft = useCallback(async () => {
    try {
      setLoading(true)
      const res = await authFetch(getLocalApiUrl(`/api/cases/${encodeURIComponent(caseId)}/forms/ecdd`))
      const json = await res.json()
      if (json.success && json.data) {
        const data = typeof json.data === 'string' ? JSON.parse(json.data) : json.data
        if (data.buckets && Array.isArray(data.buckets)) {
          setBuckets(data.buckets)
        }
        if (data.decision) setDecision(data.decision)
        if (data.decisionRationale) setDecisionRationale(data.decisionRationale)
      }
    } catch (_) { /* use defaults */ }
    finally { setLoading(false) }
  }, [caseId])

  useEffect(() => { loadDraft() }, [loadDraft])

  // Save draft to API
  const saveDraft = useCallback(async (updatedBuckets, dec, rationale) => {
    try {
      setSaving(true)
      await authFetch(getLocalApiUrl(`/api/cases/${encodeURIComponent(caseId)}/forms/ecdd`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ data: { buckets: updatedBuckets || buckets, decision: dec ?? decision, decisionRationale: rationale ?? decisionRationale } }),
      })
    } catch (_) { /* best effort */ }
    finally { setSaving(false) }
  }, [caseId, buckets, decision, decisionRationale])

  // S8-F1c — marking a mandatory item as N/A routes through the skip-reason
  // modal. Other transitions (and non-mandatory items) fire the save
  // immediately. Transitioning AWAY from 'na' clears any prior skip_reason
  // fields so the item re-enters normal flow clean.
  const [pendingSkip, setPendingSkip] = useState(null)
  const [skipSubmitting, setSkipSubmitting] = useState(false)
  const handleStatusChange = (itemId, newStatus) => {
    const targetItem = buckets.flatMap(b => b.items).find(i => i.id === itemId)
    const isMandatoryNa = targetItem?.mandatory === true && String(newStatus).toLowerCase() === 'na'
    if (isMandatoryNa) {
      setPendingSkip({ itemId, itemLabel: targetItem?.label || itemId })
      return
    }
    setSavingItemId(itemId)
    const updated = buckets.map(b => ({
      ...b,
      items: b.items.map(i => {
        if (i.id !== itemId) return i
        const next = { ...i, status: newStatus }
        // Leaving 'na' clears any previously captured skip reason fields so
        // the backend validator stops seeing them once the item is active.
        if (String(newStatus).toLowerCase() !== 'na') {
          delete next.skip_reason_code
          delete next.skip_reason_notes
        }
        return next
      }),
    }))
    setBuckets(updated)
    saveDraft(updated, null, null).finally(() => setSavingItemId(null))
  }
  const confirmSkip = async ({ code, notes }) => {
    if (!pendingSkip) return
    setSkipSubmitting(true)
    const { itemId } = pendingSkip
    const updated = buckets.map(b => ({
      ...b,
      items: b.items.map(i => i.id === itemId ? {
        ...i,
        status: 'na',
        skip_reason_code: code,
        skip_reason_notes: notes || undefined,
      } : i),
    }))
    setBuckets(updated)
    try {
      await saveDraft(updated, null, null)
      setPendingSkip(null)
    } catch (_) {
      // saveDraft already logs; keep modal open so the CO can retry.
    } finally {
      setSkipSubmitting(false)
    }
  }

  const handleFindingsChange = (itemId, findings) => {
    setSavingItemId(itemId)
    const updated = buckets.map(b => ({
      ...b,
      items: b.items.map(i => i.id === itemId ? { ...i, findings } : i),
    }))
    setBuckets(updated)
    saveDraft(updated, null, null).finally(() => setSavingItemId(null))
  }

  const handleDocumentsChange = (itemId, fileNames) => {
    if (!fileNames?.length) return
    setSavingItemId(itemId)
    const updated = buckets.map(b => ({
      ...b,
      items: b.items.map(i => {
        if (i.id !== itemId) return i
        if (i.isKyc) return { ...i, kycDocs: [...(i.kycDocs || []), ...fileNames] }
        return { ...i, docs: [...(i.docs || []), ...fileNames] }
      }),
    }))
    setBuckets(updated)
    saveDraft(updated, null, null).finally(() => setSavingItemId(null))
  }

  const handleSubmitDecision = async () => {
    if (!decision || !decisionRationale.trim() || submitting) return
    try {
      setSubmitting(true)
      await saveDraft(buckets, decision, decisionRationale)
      // Log activity
      await authFetch(getLocalApiUrl(`/api/cases/${encodeURIComponent(caseId)}/activities`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          activity_type: 'STATUS_CHANGE',
          title: `ECDD decision: ${DECISION_OPTIONS.find(d => d.key === decision)?.label || decision}`,
          description: decisionRationale.trim(),
          created_by: 'web_user',
          created_by_name: 'Web User',
        }),
      })
    } catch (_) {} finally { setSubmitting(false) }
  }

  const allItems = buckets.flatMap(b => b.items)
  const totalItems = allItems.length
  const doneItems = allItems.filter(i => i.status === 'done' || i.status === 'na').length
  const mandatoryItems = allItems.filter(i => i.mandatory)
  const mandatoryDone = mandatoryItems.filter(i => isMandatoryResolved(i.status)).length
  const allMandatoryDone = mandatoryDone === mandatoryItems.length
  const pct = totalItems > 0 ? Math.round((doneItems / totalItems) * 100) : 0

  if (loading) {
    return <div className="py-12 text-center text-sm text-slate-400">Loading ECDD…</div>
  }

  return (
    <div>
      <SkipReasonModal
        open={!!pendingSkip}
        itemLabel={pendingSkip?.itemLabel}
        submitting={skipSubmitting}
        onCancel={() => { if (!skipSubmitting) setPendingSkip(null) }}
        onConfirm={confirmSkip}
      />
      {/* Header */}
      <div className="flex items-center gap-2 mb-4">
        <Shield size={18} color="#7c3aed" />
        <span className="text-base font-bold text-slate-800">Enhanced Customer Due Diligence</span>
        <Badge bg="#f5f3ff" color="#7c3aed" border="#ddd6fe">ECDD Phase</Badge>
      </div>

      {/* Progress bar */}
      <div className="bg-white border border-slate-200 rounded-lg p-4 mb-4">
        <div className="flex justify-between items-center mb-2">
          <span className="text-xs font-bold text-slate-600 uppercase tracking-wide">ECDD Progress</span>
          <span className={`text-lg font-bold ${allMandatoryDone ? 'text-green-600' : ''}`} style={{ color: allMandatoryDone ? undefined : P.pri }}>{pct}%</span>
        </div>
        <div className="h-2 rounded-full bg-slate-100 overflow-hidden mb-2">
          <div className="h-full rounded-full transition-all duration-300" style={{ width: `${pct}%`, background: allMandatoryDone ? '#16a34a' : P.pri }} />
        </div>
        <div className="text-xs text-slate-500">
          Items: <strong className="text-slate-700">{doneItems}/{totalItems}</strong>
          <span className="mx-1.5">·</span>
          <span className={allMandatoryDone ? 'text-green-600' : 'text-red-600'}>
            Required: <strong>{mandatoryDone}/{mandatoryItems.length}</strong>
          </span>
        </div>
      </div>

      {/* Quick Checks — evidence-gathering tools */}
      <div className="mb-4">
        <QuickChecksPanel caseId={caseId} patronName={memberName} memberId={memberId} />
      </div>

      {/* Checklist buckets */}
      {buckets.map(bucket => (
        <BucketSection key={bucket.id} caseId={caseId} bucket={bucket} onStatusChange={handleStatusChange} onFindingsChange={handleFindingsChange} onDocumentsChange={handleDocumentsChange} savingId={savingItemId} />
      ))}

      {/* Decision panel */}
      <div className={`bg-white border rounded-lg p-4 mt-4 ${allMandatoryDone ? 'border-green-200' : 'border-slate-200 opacity-60'}`}>
        <div className="flex gap-2 items-center mb-3">
          <CheckCircle2 size={16} className={allMandatoryDone ? 'text-green-600' : 'text-slate-400'} />
          <span className={`text-sm font-bold ${allMandatoryDone ? '' : 'text-slate-500'}`} style={{ color: allMandatoryDone ? P.pri : undefined }}>Record Decision</span>
          {!allMandatoryDone && <span className="text-xs text-red-600">Complete all required items first</span>}
        </div>
        {allMandatoryDone ? (
          <>
            <div className="flex gap-2 mb-3">
              {DECISION_OPTIONS.map(d => (
                <div key={d.key} onClick={() => setDecision(d.key)}
                  className="flex-1 p-3 rounded-lg cursor-pointer text-center transition-colors border-2"
                  style={{ background: decision === d.key ? `${d.color}10` : '#fafbfc', borderColor: decision === d.key ? d.color : '#f1f5f9' }}>
                  <div className="text-xs font-bold" style={{ color: d.color }}>{d.label}</div>
                  <div className="text-[11px] text-slate-500 mt-1">{d.desc}</div>
                </div>
              ))}
            </div>
            {decision && (
              <div className="mb-3">
                <div className="text-xs font-semibold text-slate-600 mb-1.5">Decision Rationale</div>
                <textarea value={decisionRationale} onChange={e => setDecisionRationale(e.target.value)}
                  placeholder="Document your reasoning for this decision..."
                  className="w-full min-h-[80px] p-3 rounded-lg border border-slate-200 text-sm text-slate-700 bg-white resize-y box-border" />
              </div>
            )}
            {decision && (
              <button type="button" onClick={handleSubmitDecision} disabled={submitting || !decisionRationale.trim()}
                className="py-2.5 px-5 rounded-md border-none text-sm font-bold text-white flex items-center gap-2 cursor-pointer disabled:opacity-50"
                style={{ background: P.pri }}>
                <Shield size={14} /> {submitting ? 'Submitting…' : 'Submit for Manager Approval'}
              </button>
            )}
          </>
        ) : (
          <div className="py-6 text-center text-slate-500 text-xs">
            {mandatoryItems.length - mandatoryDone} required item{mandatoryItems.length - mandatoryDone > 1 ? 's' : ''} remaining before you can record a decision.
          </div>
        )}
      </div>
    </div>
  )
}
